import { useEffect, memo } from 'react';
// mantine
import { useIntersection, useWindowScroll } from '@mantine/hooks';
// components
import type { IAct } from '@newo/api/gateway';
import UserMessage from '@/modules/Chat/components/UserMessage';
import AgentMessage from '@/modules/Chat/components/AgentMessage';
import EventMessage from '@/modules/Chat/components/EventMessage';
// utils
// assets
import styles from '@/pages/History/components/Messages/Messages.module.css';

interface MessagesProps {
    items: IAct[];
    hasNextPage: boolean;
    fetchNextPage: () => unknown;
    pagesNumber: number;
    isFetching: boolean;
}

const SCROLL_Y_OFFSET = 10;
const SCROLL_Y_TOP = 0;
const FIRST_PAGE = 1;

const Messages = ({
    items,
    hasNextPage,
    fetchNextPage,
    pagesNumber,
    isFetching
}: MessagesProps) => {
    const [scroll, scrollTo] = useWindowScroll();
    const { ref, entry } = useIntersection({
        root: undefined,
        threshold: 0,
        rootMargin: '1000px 0px 0px 0px'
    });

    useEffect(() => {
        if (scroll.y === SCROLL_Y_TOP && hasNextPage) {
            scrollTo({ y: SCROLL_Y_OFFSET });
        }
    }, [hasNextPage, scroll.y, scrollTo]);

    useEffect(() => {
        if (entry?.isIntersecting && hasNextPage && !isFetching) {
            fetchNextPage();
        }
    }, [entry?.isIntersecting, fetchNextPage, hasNextPage, isFetching]);

    useEffect(() => {
        if (pagesNumber === FIRST_PAGE) {
            window.scrollTo(SCROLL_Y_TOP, document.body.scrollHeight);
        }
    }, [pagesNumber, scrollTo]);

    return (
        <>
            <div ref={ref} />
            <div className={styles.messages}>
                {items.map((item) => {
                    switch (item.reference_idn) {
                        case 'agent_message': {
                            return <AgentMessage act={item} />;
                        }
                        case 'user_message': {
                            return <UserMessage act={item} />;
                        }
                        default: {
                            return <EventMessage act={item} />;
                        }
                    }
                })}
            </div>
        </>
    );
};

export default memo(Messages);
